import { TZDateMini } from '@date-fns/tz';
import { getDaysInMonth, isValid, startOfDay } from 'date-fns';
import { isNil } from 'lodash-es';
import {
  type MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import type { TimeValue } from '../../../core/types/time.js';
import { replaceSecondFractions } from '../../forms-core/time/date-time.js';
import { getDayPeriodValues } from '../../forms-core/time/helpers.js';
import { getTimeValue } from '../../forms-core/time/timeParser.js';
import { SpinButtonGroup } from '../../spin-buttons/spin-button-group/SpinButtonGroup.js';
import type { SpinButtonHandler } from '../../spin-buttons/spin-button-group/types.js';
import type { DateTimePickerProps } from '../types.js';
import { useDateTimeFormat } from './useDateTimeFormat.js';
import { useNumberSpinButton } from '../../spin-buttons/hooks/useNumberSpinButton.js';
import { useTextSpinButton } from '../../spin-buttons/hooks/useTextSpinButton.js';
/**
 * Returns a list of preconfigured spinbuttons for a given datetime and format configuration.
 */
export function useSpinButtons(
  isoDateTime: TimeValue | string | null,
  type: DateTimePickerProps['type'],
  precision: DateTimePickerProps['precision'],
  isDisabled: boolean,
  isReadonly: boolean,
  ariaLabelPrefix: string | undefined,
  value?: TimeValue | string | null,
) {
  const intl = useIntl();
  const prefix = ariaLabelPrefix ? `${ariaLabelPrefix} ` : '';

  const { toParts, toDateTime, emptyParts } = useDateTimeFormat(
    type,
    precision,
  );
  const timeValue = useMemo(() => getTimeValue(isoDateTime), [isoDateTime]);
  const dayPeriodValues = useMemo(() => getDayPeriodValues(), []);
  const timezone = getTimezone();

  const [parts, setParts] = useState(
    timeValue !== null && timeValue !== undefined
      ? toParts(new TZDateMini(timeValue.absoluteDate, timezone))
      : emptyParts,
  );
  const hasDayPeriod = useMemo(
    () => parts.some((p) => p.type === 'dayPeriod'),
    [parts],
  );

  const precisionRef = useRef(precision);

  const dateTime = useMemo(() => {
    const newDateTime = toDateTime(parts);
    if (!newDateTime || !isValid(newDateTime)) {
      return newDateTime;
    }

    if (precision === precisionRef.current && precision === 'milliseconds') {
      return newDateTime;
    }

    const date = new TZDateMini(
      getTimeValue(value, precision, type)?.absoluteDate ??
        startOfDay(TZDateMini.tz(timezone)).toISOString(),
      timezone,
    );
    const milliseconds = date.getMilliseconds();
    const seconds = date.getUTCSeconds();

    if (
      precision !== precisionRef.current ||
      precision === 'minutes' ||
      precision === undefined
    ) {
      newDateTime.setUTCSeconds(seconds);
      precisionRef.current = precision;
    }

    return new TZDateMini(
      replaceSecondFractions(
        newDateTime.toISOString(),
        milliseconds.toString(),
      ),
      timezone,
    );
  }, [toDateTime, parts, precision, value, timezone]);

  const daysInMonth =
    dateTime !== null && isValid(dateTime) ? getDaysInMonth(dateTime) : 31;

  const updatePart = useCallback(
    (type: Intl.DateTimeFormatPart['type'] | undefined, value: string) => {
      setParts((prevParts) => {
        const index = prevParts.findIndex((p) => p.type === type);

        // Ignore invalid part type or if value is unchanged
        if (
          index < 0 ||
          prevParts[index].value === value ||
          type === undefined
        ) {
          return prevParts;
        }

        // Clone the parts and update entry with new value
        const newParts = [...prevParts];
        newParts[index] = { type, value };
        return newParts;
      });
    },
    [],
  );

  const yearProps = useNumberSpinButton({
    displayFormat: {
      length: 4,
    },
    required: false,
    disabled: isDisabled,
    maxValue: 9999,
    minValue: 0,
    readOnly: isReadonly,
    value: parseInt(parts.find((p) => p.type === 'year')?.value ?? '0'),
    onChange: (value) => {
      updatePart('year', value?.toString() ?? '');
    },
  });
  const monthProps = useNumberSpinButton({
    displayFormat: {
      length: 2,
    },
    required: false,
    disabled: isDisabled,
    maxValue: 12,
    minValue: 1,
    readOnly: isReadonly,
    value: parseInt(parts.find((p) => p.type === 'month')?.value ?? '0'),
    onChange: (value) => {
      updatePart('month', value?.toString() ?? '');
    },
  });
  const dayProps = useNumberSpinButton({
    displayFormat: {
      length: 2,
    },
    required: false,
    disabled: isDisabled,
    maxValue: daysInMonth,
    minValue: 1,
    readOnly: isReadonly,
    value: parseInt(parts.find((p) => p.type === 'day')?.value ?? '0'),
    onChange: (value) => {
      updatePart('day', value?.toString() ?? '');
    },
  });
  const hourProps = useNumberSpinButton({
    displayFormat: {
      length: 2,
    },
    required: false,
    disabled: isDisabled,
    maxValue: hasDayPeriod ? 12 : 23,
    minValue: hasDayPeriod ? 1 : 0,
    readOnly: isReadonly,
    value: parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0'),
    onChange: (value) => {
      updatePart('hour', value?.toString() ?? '');
    },
  });
  const minuteProps = useNumberSpinButton({
    displayFormat: {
      length: 2,
    },
    required: false,
    disabled: isDisabled,
    maxValue: 59,
    minValue: 0,
    readOnly: isReadonly,
    value: parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0'),
    onChange: (value) => {
      updatePart('minute', value?.toString() ?? '');
    },
  });
  const secondsProps = useNumberSpinButton({
    displayFormat: {
      length: 2,
    },
    required: false,
    disabled: isDisabled,
    maxValue: 59,
    minValue: 0,
    readOnly: isReadonly,
    value: parseInt(parts.find((p) => p.type === 'second')?.value ?? '0'),
    onChange: (value) => {
      updatePart('second', value?.toString() ?? '');
    },
  });
  const fractionalSecondProps = useNumberSpinButton({
    displayFormat: {
      length: 3,
    },
    required: false,
    disabled: isDisabled,
    maxValue: 999,
    minValue: 0,
    readOnly: isReadonly,
    value: parseInt(
      parts.find((p) => p.type === 'fractionalSecond')?.value ?? '0',
    ),
    onChange: (value) => {
      updatePart('fractionalSecond', value?.toString() ?? '');
    },
  });
  const dayPeriodProps = useTextSpinButton({
    options: dayPeriodValues,
    required: false,
    disabled: isDisabled,
    readOnly: isReadonly,
    value: parts.find((p) => p.type === 'dayPeriod')?.value,
    onChange: (value) => {
      if (value === null) {
        updatePart?.('dayPeriod', '');
        return;
      }
      updatePart?.('dayPeriod', value);
    },
  });

  const { spinButtons, handlers } = useMemo(() => {
    // Keep a map of all the imperative handlers per spinbutton
    const handlers = new Map<
      Intl.DateTimeFormatPartTypes,
      SpinButtonHandler | null
    >();

    const combineRef = (
      handler: SpinButtonHandler | null,
      ref: RefObject<SpinButtonHandler | null> | null,
      type: Intl.DateTimeFormatPart['type'],
    ) => {
      handlers?.set?.(type, handler);
      if (ref !== null) {
        (ref as MutableRefObject<SpinButtonHandler | null>).current = handler;
      }
    };

    // Create an array of preconfigured spinbuttons for each datetime part.
    const spinButtons = parts.map(({ type, value }, index) => {
      switch (type) {
        case 'year':
          return (
            <SpinButtonGroup.Number
              {...yearProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                combineRef(handler, yearProps.ref, type);
              }}
              value={
                yearProps.value !== undefined
                  ? yearProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}year',
                  id: 'nZk5lg177+CQcO/Z',
                  description: 'The aria label for the year input of a date.',
                },
                {
                  prefix,
                },
              )}
              placeholder={intl.formatMessage({
                defaultMessage: 'yyyy',
                id: 'c6bnAmHe/EY7/jQS',
                description: 'The abbreviation of the year part of a date',
              })}
            />
          );
        case 'month':
          return (
            <SpinButtonGroup.Number
              {...monthProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                combineRef(handler, monthProps.ref, type);
              }}
              value={
                monthProps.value !== undefined
                  ? monthProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}month',
                  id: 'LoBSMcHvTQEABp39',
                  description: 'The aria label for the month input of a date.',
                },
                {
                  prefix,
                },
              )}
              placeholder={intl.formatMessage({
                defaultMessage: 'mm',
                id: 'LnDfRkxwzq9Vq/up',
                description: 'The abbreviation of the month part of a date',
              })}
            />
          );
        case 'day':
          return (
            <SpinButtonGroup.Number
              {...dayProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                combineRef(handler, dayProps.ref, type);
              }}
              value={
                dayProps.value !== undefined ? dayProps.value : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}day',
                  id: 'Dz8K7VMW5UFpwetG',
                  description: 'The aria label for the day input of a date.',
                },
                {
                  prefix,
                },
              )}
              placeholder={intl.formatMessage({
                defaultMessage: 'dd',
                id: 'akyhaFko0pgLR1Q5',
                description: 'The abbreviation of the day part of a date',
              })}
            />
          );
        case 'hour':
          return (
            <SpinButtonGroup.Number
              {...hourProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                combineRef(handler, hourProps.ref, type);
              }}
              value={
                hourProps.value !== undefined
                  ? hourProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}hour',
                  id: 'DXoXEb27LMuapbC4',
                  description: 'The aria label for the hour input of a time.',
                },
                {
                  prefix,
                },
              )}
              placeholder={intl.formatMessage({
                defaultMessage: 'hh',
                id: '8ioXZP1LdljRBssM',
                description: 'The abbreviation of the hour part of a time',
              })}
            />
          );
        case 'minute':
          return (
            <SpinButtonGroup.Number
              {...minuteProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                combineRef(handler, minuteProps.ref, type);
              }}
              value={
                minuteProps.value !== undefined
                  ? minuteProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}minute',
                  id: 'pGR/6J+2uPcvCSju',
                  description: 'The aria label for the minute input of a time.',
                },
                {
                  prefix,
                },
              )}
              placeholder={intl.formatMessage({
                defaultMessage: 'mm',
                id: 'Uk/Q/HKAITavgOfU',
                description: 'The abbreviation of the minute part of a time',
              })}
            />
          );
        case 'second':
          return (
            <SpinButtonGroup.Number
              {...secondsProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                combineRef(handler, secondsProps.ref, type);
              }}
              value={
                secondsProps.value !== undefined
                  ? secondsProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}second',
                  id: 'WD6kswCbAgSXwMnO',
                  description: 'The aria label for the second input of a time.',
                },
                {
                  prefix,
                },
              )}
              placeholder={intl.formatMessage({
                defaultMessage: 'ss',
                id: 'wnjOChhd1Lw9s9Gg',
                description: 'The abbreviation of the second part of a time',
              })}
            />
          );
        case 'fractionalSecond':
          return (
            <SpinButtonGroup.Number
              {...fractionalSecondProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                combineRef(handler, fractionalSecondProps.ref, type);
              }}
              value={
                fractionalSecondProps.value !== undefined
                  ? fractionalSecondProps.value
                  : parseInt(value)
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}millisecond',
                  id: 'wa4ct2vRPvqd0Uif',
                  description:
                    'The aria label for the millisecond input of a time.',
                },
                {
                  prefix,
                },
              )}
              placeholder={intl.formatMessage({
                defaultMessage: 'fff',
                id: 'ZSJMoZevK9FVBtMR',
                description:
                  'The abbreviation of the fractional of a second part of a time',
              })}
            />
          );
        case 'dayPeriod':
          return (
            <SpinButtonGroup.Text
              {...dayPeriodProps}
              key={type}
              ref={(handler: SpinButtonHandler | null) => {
                combineRef(handler, dayPeriodProps.ref, type);
              }}
              value={
                dayPeriodProps.value !== undefined
                  ? dayPeriodProps.value
                  : value
              }
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{prefix}day period',
                  id: 'a9uVGc0cPE1OqAJj',
                  description:
                    'The aria label for the day period (am/pm) input of a time.',
                },
                {
                  prefix,
                },
              )}
              placeholder={intl.formatMessage({
                defaultMessage: 'aa',
                id: '8I3jLlYKntZOjiy8',
                description:
                  'The abbreviation for the day period (am/pm) part of a time',
              })}
            />
          );
        case 'literal':
          return isNil(value) ? null : (
            // Show the whole literal but only use the first char as trigger.
            <SpinButtonGroup.Separator
              key={`${type}_${index}`}
              trigger={value[0]}
            >
              {value}
            </SpinButtonGroup.Separator>
          );
        default:
          // For unknown part types, simply render the value as textnode in the DOM.
          return value;
      }
    });

    return { handlers, spinButtons };
    // Do not recreate the spinbuttons on parts update. This prevents prefix issues when starting values with 0.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDisabled,
    isReadonly,
    updatePart,
    daysInMonth,
    hasDayPeriod,
    parts.length,
    yearProps.value,
    monthProps.value,
    dayProps.value,
    hourProps.value,
    minuteProps.value,
    secondsProps.value,
    fractionalSecondProps.value,
    dayPeriodProps.value,
  ]);

  // Callback to push the given part values to the corresponding spinbuttons.
  const updateParts = useCallback(
    (updatedParts: Intl.DateTimeFormatPart[]) => {
      const nonLiteralParts = updatedParts.filter(
        ({ type }) => type !== 'literal',
      );

      const lookup = new Map(
        nonLiteralParts.map(({ type, value }) => [type, value]),
      );

      // Apply the new values to the spinbuttons.
      // Would not be necessary, if the spinbuttons would be controlled. But this is not possible because this
      // interferes with the expected typing behavior for spinbuttons with format patterns like for this one used for the datetime parts.
      nonLiteralParts.forEach((part) => {
        const handler = handlers.get(part.type);
        if (part.value === '') {
          handler?.clearValue();
        } else if (handler?.type === 'number') {
          handler.applyText(part.value, true);
        } else if (handler?.type === 'text') {
          handler.searchValue(part.value[0], true);
        }
      });

      // update the parts with new values
      setParts((prevParts) => {
        return prevParts.map((part) => {
          const newValue = lookup.get(part.type);
          return newValue !== undefined ? { ...part, value: newValue } : part;
        });
      });
    },
    [handlers],
  );

  // If the format, type or precision is changing, restore the prev. values when generating a set of new spinbuttons.
  useEffect(() => {
    setParts(() => (dateTime === null ? emptyParts : toParts(dateTime)));
    // Need to update the parts on precision change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emptyParts, toDateTime, toParts, precision]);

  return {
    spinButtons,
    dateTime,
    updateParts,
    handlers,
    parts,
    toDateTime,
  };
}
